import { SearchResults, SearchState, ViewTypeLabel } from '../interfaces/search.interface';
import { createSlice } from '@reduxjs/toolkit';
import { removeUnavailableFacets } from '../utils/facet.util';

/*----- CREATE STORE ------*/
declare interface SearchSlice {
  name: string;
  initialState: SearchState;
  reducers: any;
}

const slice: SearchSlice = {
  name: 'search',
  initialState: {
    search: {
      data: undefined,
      request: undefined,
      isLoading: false,
      isLoaded: false,
      isSuccess: false,
      isFailure: false,
      message: 'No search performed 1',
      view: ViewTypeLabel.LIST,
      isCustomISBN: false
    },
    predefinedFilters: [],
    selectedCatalog: undefined,
    searchPageType: undefined,
    currentPageIndex: undefined,
    currentPageLength: undefined,
    blockedCount: 0,
  },
  reducers: {
    onSuccess: (state: SearchState, { payload }: { payload: SearchResults }) => {
      state.search = {
        data: payload,
        request: state?.search?.request ? removeUnavailableFacets(state.search.request, payload) : undefined,
        isLoaded: true,
        isLoading: false,
        isSuccess: true,
        isFailure: false,
        message: `Successfully loaded search`,
        view: state?.search?.view,
        isCustomISBN: state?.search?.isCustomISBN
      };
    },
    onFailure: (state: SearchState) => {
      state.search = {
        data: undefined,
        request: state?.search?.request,
        isLoaded: false,
        isLoading: false,
        isSuccess: false,
        isFailure: true,
        message: `Failed to load search`,
        view: state?.search?.view,
        isCustomISBN: false,
      };
    },
    setLoading: (state: SearchState, action: any) => {
      state.search = {
        data: state?.search?.data,
        request: action.payload,
        isLoaded: state?.search?.isLoaded,
        isLoading: true,
        isSuccess: false,
        isFailure: state?.search?.isFailure,
        message: `Loading search results`,
        view: state?.search?.view,
      };
    },
    setSelectedCatalog: (state: SearchState, action: any) => {
      state.selectedCatalog = action.payload;
    },
    setSearchTerm: (state: SearchState, action: any) => {
      if (! state.search.request) {
        state.search.request = {
          searchTerms: action.payload,
          catalog: 'unified',
          pageLength: 0,
          start: 0,
          facets: false,
        };
      } else {
        state.search.request.searchTerms = action.payload;
      }
    },
    setListView: (state: SearchState) => {
      state.search = {
        ...state.search,
        view: ViewTypeLabel.LIST,
      };
    },
    setCardView: (state: SearchState) => {
      state.search = {
        ...state.search,
        view: ViewTypeLabel.CARD,
      };
    },
    setIsCustomISBN: (state: SearchState, action: any) => {
      state.search.isCustomISBN = action.payload;
    },
    setPredefinedFilters: (state: SearchState, action: any) => {
      state.predefinedFilters = action.payload;
    },
    setSearchPageType: (state: SearchState, action: any) => {
      state.searchPageType = action.payload;
    },
    setCurrentPageIndex: (state: SearchState, action: any) => {
      state.currentPageIndex = action.payload;
    },
    setCurrentPageLength: (state: SearchState, action: any) => {
      state.currentPageLength = action.payload;
    },
    setBlockedCount: (state: SearchState, action: any) => {
      state.blockedCount = action.payload;
    },
  },
};

export const searchSlice: any = createSlice(slice);

/*----- EXPORT ACTIONS ------*/
export const onSuccess: Function = searchSlice.actions.onSuccess;
export const onFailure: Function = searchSlice.actions.onFailure;
export const setLoading: Function = searchSlice.actions.setLoading;
export const setListView: Function = searchSlice.actions.setListView;
export const setCardView: Function = searchSlice.actions.setCardView;
export const setSelectedCatalog: Function = searchSlice.actions.setSelectedCatalog;
export const setSearchTerm: Function = searchSlice.actions.setSearchTerm;
export const setIsCustomISBN: Function = searchSlice.actions.setIsCustomISBN;
export const setPredefinedFilters: Function = searchSlice.actions.setPredefinedFilters;
export const setSearchPageType: Function = searchSlice.actions.setSearchPageType;
export const setCurrentPageIndex: Function = searchSlice.actions.setCurrentPageIndex;
export const setCurrentPageLength: Function = searchSlice.actions.setCurrentPageLength;
export const setBlockedCount: Function = searchSlice.actions.setBlockedCount;

/*----- EXPORT DEFAULT ------*/
export const searchReducer = searchSlice.reducer;
